import { useState } from 'react'
import { Download, CheckCircle, AlertCircle, FileText, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useExportData } from '@/hooks'

interface DataSectionProps {
  userId?: string
}

export function DataSection({ userId: _userId }: DataSectionProps) {
  const { exportData, loading, error } = useExportData()
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleExport = async () => {
    // Call API to export data
    const data = await exportData()

    if (data) {
      // Create JSON file download
      const jsonString = JSON.stringify(data, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `user-data-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      // Show success toast
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 3000)
    } else {
      // Show error toast
      setErrorMessage(error || 'Failed to export data. Please try again.')
      setShowError(true)
      setTimeout(() => setShowError(false), 3000)
    }
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Data & Privacy
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage your personal data and privacy settings
          </p>
        </div>

        {/* GDPR Export Section */}
        <div className="border border-border/50 rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Export Your Data</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Download a copy of your personal data in JSON format. This includes your profile
                information, company details, activity log, and consent records.
              </p>

              <div className="bg-muted/50 rounded-md p-4 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                  <div>
                    <p className="font-medium">What's included in your export:</p>
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      <li>• Profile information (name, email, role, account status)</li>
                      <li>• Company details (if applicable)</li>
                      <li>• Activity log (recent actions and timestamps)</li>
                      <li>• Consent records (privacy policy, terms, marketing preferences)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleExport}
              disabled={loading}
              className="gap-2"
            >
              {loading ? (
                <>
                  <Download className="w-4 h-4 animate-pulse" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export Data
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Data Retention Info */}
        <div className="border border-border/50 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold">Data Retention</h3>
          <p className="text-muted-foreground text-sm">
            We retain your personal data only for as long as necessary to provide our services
            and comply with legal obligations. You can request account deletion at any time
            through the Security tab.
          </p>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-900 dark:text-green-100 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-medium">Data exported successfully!</p>
              <p className="text-sm text-green-700 dark:text-green-300">Your data has been downloaded.</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-100 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <div>
              <p className="font-medium">Export Failed</p>
              <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
